// Field app screen changes on a bad or missing network (card #3864, watchdog
// rework card #4506).
//
// A Turbo Drive visit is a fetch() with no timeout. Offline in a dead zone,
// or with a VPN keeping the phone "connected" while nothing leaves it, that
// fetch hung 20 to 24 seconds before Turbo gave up and fell back to a full
// load, which then waited on the service worker's 3s NetworkFirst timeout.
//
// Card #3864 fixed this with an AbortSignal.timeout() combined into the
// visit's own fetch signal. Card #4506 removed that: Turbo 8.0.23 never
// awaits the body read in FetchRequest#receive (the call to
// requestSucceededWithResponse is fire-and-forget), so a timeout firing
// after headers arrive but mid-body-read rejects that floating promise with
// nothing to catch it -- an unhandled rejection, no turbo:fetch-request-error,
// no fallback. On WebKit, even a stall before the headers arrive hangs: the
// same abort reports as a plain AbortError there (not Turbo's own
// TimeoutError), which Turbo's own error handling swallows silently with no
// reload. Production logged 179 "Screen request failed" events since the
// original fix shipped and never once from an iPhone -- the mechanism never
// actually caught the failure mode it was built for.
//
// Three changes, all for field pages only (application.js is shared with the
// admin app):
//
//   1. Known offline (field:online "false", or the browser reports no
//      connection): a Turbo visit is cancelled and becomes a full navigation
//      straight away, so the service worker serves the cached page at once.
//      Not for a visit that follows a form submission (its response is
//      already in hand).
//   2. Online: a watchdog timer (SCREEN_WATCHDOG_MS), armed on
//      turbo:before-visit and cleared only on turbo:load -- never on
//      turbo:before-render, which also fires for an instant cached-snapshot
//      preview and must not disarm a watchdog for a visit that hasn't
//      actually finished. If it fires, the visit is cancelled via Turbo's own
//      Navigator#stop (the API Turbo itself uses to supersede a visit, so a
//      watchdog-triggered cancel doesn't throw an unhandled rejection the way
//      a raw AbortSignal did), then the page does a full navigation to the
//      same URL -- the same fallback the offline short-circuit below already
//      uses.
//   3. Timing: every screen change slower than SLOW_SCREEN_MS, every offline
//      tap and every failed visit request is logged ("nav" category) with the
//      path, milliseconds, online state, connectivity level, where it came
//      from (network, cache), the browser's connection hints, the phase the
//      visit reached (fetch started, headers received, body done, render)
//      and the browser engine.
//   4. Card #4508: a full navigation the service worker answered because its
//      own 5s deadline was hit (not because the device is offline) shows a
//      one-line "saved version" notice, so a slow-but-alive connection is
//      never silently mistaken for true offline.
//
// Card #4540 (measurement only, no behaviour change): "Screen loaded" now
// logs EVERY screen change, not only ones slower than SLOW_SCREEN_MS or an
// offline tap -- a day of production data is the only way to answer what
// share of screen changes actually stall. Two fields were added: `from` (the
// path the Tasker was on before this navigation) and `latched` (whether the
// destination was served, or would have been served, under the #4310
// store/Today offline-first shortcut).
import { logSpeedEvent, SCREEN_LOADED, SCREEN_REQUEST_FAILED, SCREEN_LOAD_CANCELLED } from "./field_speed_events"
import { currentLevel, lastPingSample, LEVEL_OFFLINE, LEVEL_DEGRADED, LEVEL_ONLINE } from "../field_network_health"
import { isOfflineFirst, offlineFirstPlaceIds } from "../field_place_connectivity"
import { showFieldFlash } from "./field_flash"
import { classifyQuality } from "../pwa_network_quality"

export const SLOW_SCREEN_MS = 2000
// How long a Turbo visit to a field page may run before the watchdog cancels
// it and falls back to a full navigation. Armed on turbo:before-visit,
// cleared on turbo:load -- see the header comment above.
export const SCREEN_WATCHDOG_MS = 8000

// Carries a tap's start time across a full navigation (offline short-circuit,
// or Turbo's reload after a failed fetch) so the next page can log the whole
// wait, not just its own load.
const PENDING_KEY = "field:nav-pending"
// A saved tap older than this is not this page's (a reload long after, a
// navigation that never landed) and is discarded unlogged.
const PENDING_MAX_AGE_MS = 60_000

const LEVEL_LABEL = { [LEVEL_OFFLINE]: "offline", [LEVEL_DEGRADED]: "degraded", [LEVEL_ONLINE]: "online" }

export function isKnownOffline() {
  return localStorage.getItem("field:online") === "false" || navigator.onLine === false
}

function isFieldPath(pathname) {
  return pathname === "/field" || pathname.startsWith("/field/")
}

// Card #4540 -- mirrors field_service_worker.js's own _placeIdFromPath /
// isOfflineFirstNavigation so the "latched" field on a "Screen loaded" line
// reflects the same #4310 offline-first state the service worker's routing
// decision reads, without either side importing the other (page vs worker
// context). Today bundles every place onto one page with no place id of its
// own, same as the SW side.
const OFFLINE_FIRST_SHARED_PATHS = new Set(["/field", "/field/today"])

function placeIdFromPath(pathname) {
  const match = pathname.match(/^\/field\/places\/(\d+)(?:\/|$)/)
  return match ? Number(match[1]) : null
}

function isLatchedPath(pathname) {
  const placeId = placeIdFromPath(pathname)
  if (placeId != null) return isOfflineFirst(placeId)
  if (OFFLINE_FIRST_SHARED_PATHS.has(pathname)) return offlineFirstPlaceIds().length > 0
  return false
}

// Card #4671 / ADR-0174 decision 2 -- iPhone (webkit) exposes no Network
// Information API at all (navigator.connection is always undefined there,
// about 70% of screen loads per the ADR). connectionEstimate() below is the
// fallback for exactly that case: a measured connection class from our own
// timing, clearly labelled as an estimate rather than a browser-reported
// fact, never a second guess layered on top of real browser values.
function connectionHints(timingMs = null) {
  const c = navigator.connection
  if (c) {
    const hints = { effectiveType: c.effectiveType, rtt: c.rtt, downlink: c.downlink, saveData: c.saveData }
    return Object.fromEntries(Object.entries(hints).filter(([, v]) => v !== undefined))
  }
  return connectionEstimate(timingMs)
}

// Approximates a connection class from two signals, when the browser gives
// us none of its own:
//   1. timingMs -- this request's own headers_received phase (time to first
//      byte), when the caller has one (a Turbo visit tracks it; a full page
//      load falls back to the Navigation Timing entry, see
//      fullLoadHeadersMs() below).
//   2. the field network status poll's own periodic ping (every 10s,
//      pwa/network_status_controller.js's _poll, fed into
//      field_network_health.js by field_network_status_controller.js) --
//      ADR-0174's "small periodic probe". Reused as-is rather than adding a
//      second probe: one implementation, not two.
//
// classifyQuality is the SAME good/fair/poor/very_poor classifier
// field_network_health.js uses for its own Degraded/Online hysteresis -- the
// estimate reads on the same scale as everything else in the app, not an
// invented one of its own. connectionClassEstimated: true marks it plainly as
// an estimate, never mistaken for a browser-reported value.
//
// Says nothing (returns {}) when there is no signal at all yet (first screen
// of a session, before any ping has landed) -- a guess with no evidence
// behind it is worse than silence.
//
// Card #4671 CHANGES (3 Nov review), fix 3 -- lastPingSample() has no age
// check of its own: a page backgrounded for minutes, then foregrounded for a
// screen change, would otherwise still read its last ping as current
// evidence. freshPingSample() ignores a ping older than PING_MAX_AGE_MS,
// using its own recordedAt timestamp, and treats it exactly like no ping has
// landed yet.
const PING_MAX_AGE_MS = 60_000

function freshPingSample() {
  const ping = lastPingSample()
  if (!ping) return null
  return Date.now() - ping.recordedAt <= PING_MAX_AGE_MS ? ping : null
}

function connectionEstimate(timingMs) {
  const ping = freshPingSample()
  const pingOk = ping?.outcome === "ok"
  const samples = [timingMs, pingOk ? ping.durationMs : null].filter((v) => typeof v === "number" && v >= 0)
  const pingFailed = !!ping && !pingOk

  if (samples.length === 0 && !pingFailed) return {}

  const avgMs = samples.length ? samples.reduce((a, b) => a + b, 0) / samples.length : null
  const lossPercent = samples.length === 0 && pingFailed ? 100 : 0

  return {
    connectionClassEstimate: classifyQuality(avgMs, lossPercent),
    connectionClassEstimated: true
  }
}

// Navigation Timing's own responseStart/requestStart for the CURRENT
// document -- the only timing signal available for a full page load (no
// Turbo visit phases exist for one). Only meaningful right after that load,
// which is exactly when the "pending" full-load branch below calls it.
function fullLoadHeadersMs() {
  const entry = performance.getEntriesByType?.("navigation")?.[0]
  if (!entry) return null
  if (typeof entry.responseStart !== "number" || typeof entry.requestStart !== "number") return null
  const ms = Math.round(entry.responseStart - entry.requestStart)
  return ms >= 0 ? ms : null
}

// Chrome's UA always carries "Chrome/" even though it also spoofs "Safari/",
// so Chrome must be checked first. CriOS (Chrome on iOS) runs on WebKit under
// Apple's rules but reports itself as Chromium in its own UA token -- taken
// at face value here, since the question this answers is "which rendering
// path is Turbo exercising", and Apple's WebView policy means that's not
// actually a stable signal either way without feature detection no simpler
// than this string check.
function browserEngine() {
  const ua = navigator.userAgent || ""
  if (/Chrome\/|Chromium\/|CriOS\//.test(ua)) return "chromium"
  if (/Firefox\//.test(ua)) return "gecko"
  if (/Safari\//.test(ua)) return "webkit"
  return "unknown"
}

function logScreen(message, detail, { timingMs = null } = {}) {
  logSpeedEvent(message, {
    ...detail,
    online: localStorage.getItem("field:online") !== "false",
    deviceOnline: navigator.onLine,
    level: LEVEL_LABEL[currentLevel()] ?? null,
    ...connectionHints(timingMs)
  }).catch(() => {})
}

function rememberPending(path, startedAtEpoch, offline, fromPath) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ path, at: startedAtEpoch, offline, from: fromPath ?? null }))
  } catch { /* storage full or blocked -- timing is best effort */ }
}

function takePending() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    sessionStorage.removeItem(PENDING_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// The document's own navigation entry says whether this full load came from
// the service worker's cache or the network. Checked in Chromium: a page the
// worker answered from Cache Storage has deliveryType "cache-storage" and
// transferSize 0; one it fetched has the bytes. A browser that reports
// neither gets null rather than a guess.
function fullLoadSource() {
  const entry = performance.getEntriesByType?.("navigation")?.[0]
  if (!entry) return null
  if (entry.deliveryType === "cache-storage") return "cache"
  if (typeof entry.transferSize !== "number") return null
  return entry.transferSize === 0 ? "cache" : "network"
}

// Card #4508 -- the service worker tags a deadline-fallback navigation
// response with a Server-Timing entry (field_service_worker.js's
// tagDeadlineFallback) rather than posting a message, because there is no
// open page client yet to receive one: this IS the document load. Read off
// the same PerformanceNavigationTiming entry fullLoadSource() already uses.
function deadlineFallbackHit() {
  const entry = performance.getEntriesByType?.("navigation")?.[0]
  return !!entry?.serverTiming?.some((t) => t.name === "field-deadline")
}

// Shown once per full load, only for the new deadline branch -- true offline
// and a #4310-latched place already show their own "Offline" copy on the
// shells that need it, and are left exactly as they were.
function announceDeadlineFallback() {
  if (!deadlineFallbackHit()) return
  showFieldFlash("Showing a saved version while we reconnect", { tone: "info" })
}

// Mirrors Turbo's own choice: data-turbo-frame on the submitter or form
// names the frame ("_top" means the whole page), else the enclosing frame
// unless its target is "_top".
function submissionTargetsFrame(form, submitter) {
  if (!form) return false
  const named = submitter?.getAttribute("data-turbo-frame") || form.getAttribute("data-turbo-frame")
  if (named) return named !== "_top"
  const frame = form.closest("turbo-frame")
  return !!frame && frame.getAttribute("target") !== "_top"
}

export function installFieldNavigation() {
  // { path, startedAt (perf), startedAtEpoch, offline, watchdogId, phase,
  //   phases: { fetch_started, headers_received, body_done, render } (ms) }
  let visit = null
  let afterSubmission = false

  // A full navigation this module (or the watchdog's own fallback) started:
  // log the tap's whole wait on the page it landed on.
  const pending = takePending()
  if (pending && pending.path === window.location.pathname) {
    const ms = Date.now() - pending.at
    // Card #4540: every full-load screen change is logged now, not only a
    // slow or offline one -- only the sanity bound (not stale, not negative)
    // still gates it.
    if (ms >= 0 && ms <= PENDING_MAX_AGE_MS) {
      logScreen(SCREEN_LOADED, {
        path: pending.path, ms, source: fullLoadSource(), via: "full_load", offlineTap: !!pending.offline,
        from: pending.from ?? null, latched: isLatchedPath(pending.path)
      }, { timingMs: fullLoadHeadersMs() })
    }
  }

  if (isFieldPath(window.location.pathname)) announceDeadlineFallback()

  document.addEventListener("turbo:submit-start", () => { afterSubmission = true })
  // Only a successful Drive submission is followed by a visit. A failed one,
  // or one answered inside a frame, never reaches before-visit, so the flag
  // would otherwise spare the next ordinary tap from the offline short-cut.
  document.addEventListener("turbo:submit-end", (event) => {
    const { success, formSubmission } = event.detail || {}
    if (!success || submissionTargetsFrame(formSubmission?.formElement, formSubmission?.submitter)) {
      afterSubmission = false
    }
  })

  function clearWatchdog(v) {
    if (v?.watchdogId != null) {
      clearTimeout(v.watchdogId)
      v.watchdogId = null
    }
  }

  function markPhase(v, phase) {
    if (!v) return
    v.phase = phase
    v.phases[phase] = Math.round(performance.now() - v.startedAt)
  }

  // Armed on turbo:before-visit, cleared only on turbo:load. Deliberately not
  // cleared on turbo:before-render -- that event also fires for Turbo's
  // instant cached-snapshot preview, which is not the visit finishing.
  function armWatchdog(v, url) {
    v.watchdogId = setTimeout(() => {
      if (visit !== v) return // already superseded or resolved
      clearWatchdog(v)
      const ms = Math.round(performance.now() - v.startedAt)
      logScreen(SCREEN_REQUEST_FAILED, {
        path: v.path, ms, reason: "watchdog", phase: v.phase, phases: v.phases, engine: browserEngine()
      }, { timingMs: v.phases.headers_received ?? null })
      visit = null
      // Cancel through Turbo's own API, not a raw AbortSignal -- this is how
      // Turbo itself supersedes an in-flight visit, so the cancellation is
      // seen by Turbo's own state machine instead of leaking an unhandled
      // rejection out of its un-awaited body read (see header comment).
      try { window.Turbo?.navigator?.stop() } catch { /* best effort */ }
      rememberPending(v.path, v.startedAtEpoch, v.offline, v.fromPath)
      window.location.href = url.href
    }, SCREEN_WATCHDOG_MS)
  }

  document.addEventListener("turbo:before-visit", (event) => {
    const url = new URL(event.detail.url, window.location.href)
    if (!isFieldPath(url.pathname)) return

    const offline = isKnownOffline()
    if (visit) {
      clearWatchdog(visit)
      if (performance.now() - visit.startedAt > SLOW_SCREEN_MS) {
        logScreen(SCREEN_LOAD_CANCELLED, {
          path: visit.path, ms: Math.round(performance.now() - visit.startedAt),
          phase: visit.phase, phases: visit.phases, engine: browserEngine()
        }, { timingMs: visit.phases.headers_received ?? null })
      }
    }
    // fromPath is read here, synchronously, before Turbo (or the offline
    // short-circuit below) changes anything -- window.location.pathname still
    // names the page the Tasker is leaving (card #4540's "previous path").
    visit = {
      path: url.pathname, startedAt: performance.now(), startedAtEpoch: Date.now(), offline,
      watchdogId: null, phase: "visit_started", phases: {}, fromPath: window.location.pathname
    }

    if (offline && !afterSubmission && url.origin === window.location.origin) {
      event.preventDefault()
      rememberPending(url.pathname, visit.startedAtEpoch, true, visit.fromPath)
      visit = null
      // A service worker that restarted since this page last reported has
      // forgotten it is offline; tell it again before it sees the request.
      navigator.serviceWorker?.controller?.postMessage({ type: "CLIENT_ONLINE_STATE", online: false })
      window.location.href = url.href
    } else {
      armWatchdog(visit, url)
    }
    afterSubmission = false
  })

  document.addEventListener("turbo:before-fetch-request", (event) => {
    // Only Turbo Drive page visits: they carry no target, so dispatch lands on
    // <html>. Forms, frames and prefetches dispatch on their own element.
    if (event.target !== document.documentElement) return
    const { fetchOptions } = event.detail
    if ((fetchOptions.method || "GET").toUpperCase() !== "GET") return
    if (!isFieldPath(new URL(event.detail.url, window.location.href).pathname)) return
    markPhase(visit, "fetch_started")
  })

  document.addEventListener("turbo:before-fetch-response", (event) => {
    if (!visit || event.target !== document.documentElement) return
    markPhase(visit, "headers_received")
    // An independent read of a clone of the response -- Turbo reads its own
    // clone separately, so this never interferes with Turbo's own handling.
    // Timing only: errors (including the watchdog's own cancel) are ignored.
    const v = visit
    event.detail.fetchResponse?.response?.clone().text()
      .then(() => markPhase(v, "body_done"))
      .catch(() => {})
  })

  // Fires once the fetched page is actually rendered. Also fires for Turbo's
  // instant cached-snapshot preview -- harmless here, since this only updates
  // a timing phase and the real render's timestamp (always the one
  // immediately before turbo:load) overwrites it.
  document.addEventListener("turbo:render", () => {
    if (visit) markPhase(visit, "render")
  })

  document.addEventListener("turbo:fetch-request-error", (event) => {
    if (!visit || event.target !== document.documentElement) return
    clearWatchdog(visit)
    const ms = Math.round(performance.now() - visit.startedAt)
    logScreen(SCREEN_REQUEST_FAILED, { path: visit.path, ms, reason: "fetch_error", error: event.detail?.error?.name || null, phase: visit.phase },
      { timingMs: visit.phases.headers_received ?? null })
    // Turbo reloads next; carry the start so the landing page logs the total.
    rememberPending(visit.path, visit.startedAtEpoch, visit.offline, visit.fromPath)
    visit = null
  })

  document.addEventListener("turbo:load", () => {
    if (!visit) return
    clearWatchdog(visit)
    const ms = Math.round(performance.now() - visit.startedAt)
    // Card #4540: every Turbo screen change is logged now, not only a slow or
    // offline one.
    logScreen(SCREEN_LOADED, {
      path: visit.path, ms, source: "network", via: "turbo", offlineTap: visit.offline,
      phases: visit.phases, engine: browserEngine(), from: visit.fromPath ?? null, latched: isLatchedPath(visit.path)
    }, { timingMs: visit.phases.headers_received ?? null })
    visit = null
  })
}
